import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import logger from '@/lib/logger';
import { Leaf, Spinner, CookingPot, Clock, Lightbulb } from '@phosphor-icons/react';
import { ProGate } from '@/components/ProGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NutritionPage() {
  return (
    <ProGate feature="AI Nutrition Recommendations">
      <NutritionForm />
    </ProGate>
  );
}

function NutritionForm() {
  const { user } = useAuth();
  const [goal, setGoal] = useState(user?.goals?.[0] || 'fat_loss');
  const [diet, setDiet] = useState(user?.dietary_preferences || 'balanced');
  const [meals, setMeals] = useState(3);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState('');

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/nutrition/recommend`, {
        goal: goal.replace('_', ' '),
        dietary_preferences: diet,
        meals_per_day: meals,
      });
      setPlan(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate nutrition plan.');
    } finally {
      setLoading(false);
    }
  };

  const rec = plan?.recommendations;

  return (
    <div data-testid="nutrition-page">
      <div className="mb-8">
        <div className="label-tactical text-[#FF3B30] mb-1">AI Nutrition</div>
        <h1 className="text-4xl font-black tracking-tighter">MEAL RECOMMENDATIONS</h1>
        <p className="text-[#A1A1AA] mt-2">Simple, practical meal guidance tailored to your goals.</p>
      </div>

      {!plan && (
        <div className="max-w-2xl">
          <div className="mb-6">
            <div className="label-tactical mb-3">Your Goal</div>
            <div className="flex flex-wrap gap-2">
              {['fat_loss', 'muscle_tone', 'endurance', 'stress_relief'].map(g => (
                <button key={g} onClick={() => setGoal(g)} data-testid={`nutrition-goal-${g}`}
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-all ${goal === g ? 'bg-[#FF3B30] text-white' : 'tactical-card text-[#A1A1AA] hover:border-white/20'}`}>
                  {g.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <div className="label-tactical mb-3">Dietary Preference</div>
            <div className="flex flex-wrap gap-2">
              {['balanced', 'vegetarian', 'vegan', 'keto', 'high_protein'].map(d => (
                <button key={d} onClick={() => setDiet(d)} data-testid={`nutrition-diet-${d}`}
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-all ${diet === d ? 'bg-[#FF3B30] text-white' : 'tactical-card text-[#A1A1AA] hover:border-white/20'}`}>
                  {d.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <div className="label-tactical mb-3">Meals Per Day</div>
            <div className="flex gap-2">
              {[2, 3, 4, 5].map(m => (
                <button key={m} onClick={() => setMeals(m)} data-testid={`nutrition-meals-${m}`}
                  className={`px-5 py-3 text-lg font-bold transition-all ${meals === m ? 'bg-[#FF3B30] text-white' : 'tactical-card text-[#A1A1AA] hover:border-white/20'}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] px-4 py-3 mb-6 text-sm">
              {error}
            </div>
          )}

          <button onClick={generate} disabled={loading} className="btn-primary px-8 py-4 text-lg flex items-center gap-2 disabled:opacity-50" data-testid="generate-nutrition-button">
            {loading ? (
              <><Spinner className="w-5 h-5 animate-spin" /> Generating...</>
            ) : (
              <><Leaf weight="bold" className="w-5 h-5" /> Get Recommendations</>
            )}
          </button>
        </div>
      )}

      {plan && rec && (
        <div data-testid="nutrition-plan">
          <div className="tactical-card p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold tracking-tight">{rec.plan_name || 'Your Nutrition Plan'}</h2>
                <p className="text-[#A1A1AA] text-sm mt-1">{rec.description}</p>
              </div>
              <button onClick={() => setPlan(null)} className="btn-secondary px-4 py-2 text-sm" data-testid="new-nutrition-button">
                New Plan
              </button>
            </div>
            {rec.daily_framework && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                {rec.daily_framework.calories_range && (
                  <div className="bg-[#1C1C1E] p-3 border border-white/5">
                    <div className="label-tactical text-[#FF3B30] mb-1">Calories</div>
                    <p className="text-sm">{rec.daily_framework.calories_range}</p>
                  </div>
                )}
                {rec.daily_framework.protein_tip && (
                  <div className="bg-[#1C1C1E] p-3 border border-white/5">
                    <div className="label-tactical text-[#34C759] mb-1">Protein</div>
                    <p className="text-sm">{rec.daily_framework.protein_tip}</p>
                  </div>
                )}
                {rec.daily_framework.hydration && (
                  <div className="bg-[#1C1C1E] p-3 border border-white/5">
                    <div className="label-tactical text-[#007AFF] mb-1">Hydration</div>
                    <p className="text-sm">{rec.daily_framework.hydration}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {(rec.meals || []).map((meal) => (
            <div key={meal.meal} className="tactical-card p-5 mb-4" data-testid={`meal-${meal.meal}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-bold tracking-tight">{meal.meal}</h3>
                {meal.time && <span className="text-sm text-[#71717A]">{meal.time}</span>}
              </div>
              <div className="space-y-2">
                {(meal.options || []).map((opt, j) => (
                  <div key={opt.name || `opt-${j}`} className="bg-[#1C1C1E] p-3 border border-white/5">
                    <div className="font-medium mb-1">{opt.name}</div>
                    {opt.ingredients && (
                      <div className="flex flex-wrap gap-1 mb-1">
                        {opt.ingredients.map((ing, k) => (
                          <span key={ing} className="text-xs bg-white/5 px-2 py-0.5 text-[#A1A1AA]">{ing}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-4 text-xs text-[#71717A]">
                      {opt.prep_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {opt.prep_time}</span>}
                      {opt.tip && <span className="flex items-center gap-1"><Lightbulb className="w-3 h-3" /> {opt.tip}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {rec.weekly_tips && rec.weekly_tips.length > 0 && (
            <div className="tactical-card p-5">
              <h3 className="text-xl font-bold tracking-tight mb-3">WEEKLY TIPS</h3>
              <ul className="space-y-2">
                {rec.weekly_tips.map((tip, idx) => (
                  <li key={`tip-${idx}`} className="flex items-start gap-2 text-sm text-[#A1A1AA]">
                    <Lightbulb weight="bold" className="w-4 h-4 text-[#FF9500] mt-0.5 flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
